import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook, Mail } from 'lucide-react';
import { SNIFFSPOT_LOGO_URL } from '../utils/constants';

const MarketingLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path ? "text-emerald-700 font-bold" : "text-gray-600 hover:text-emerald-600";

  return (
    <div className="min-h-screen bg-gradient-to-br from-lime-200 via-green-100 to-blue-100 font-sans flex flex-col">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm sticky top-0 z-50 shadow-sm border-b border-emerald-100">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src={SNIFFSPOT_LOGO_URL} 
                alt="Logo" 
                className="h-10 w-auto rounded-full"
                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/22c55e/ffffff?text=Shep"; }}
              />
              <span className="font-display text-2xl text-emerald-800 tracking-wide hidden sm:block">Shep's Sniff Spot</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={isActive('/')}>Home</Link>
              <Link to="/the-spot" className={isActive('/the-spot')}>The Spot</Link>
              <Link to="/shop" className={isActive('/shop')}>Shop</Link>
              <Link to="/about" className={isActive('/about')}>About Shep</Link>
              <Link to="/rules" className={isActive('/rules')}>Rules</Link>
              <Link to="/pricing" className={isActive('/pricing')}>Pricing</Link>
              <Link to="/contact" className={isActive('/contact')}>Contact</Link>
              
              <Link 
                to="/app" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-full font-bold shadow-md transition-transform transform hover:scale-105"
              >
                Login / Book
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-emerald-800">
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4 flex flex-col items-center bg-white rounded-lg shadow-lg p-4">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className={isActive('/')}>Home</Link>
              <Link to="/the-spot" onClick={() => setIsMenuOpen(false)} className={isActive('/the-spot')}>The Spot</Link>
              <Link to="/shop" onClick={() => setIsMenuOpen(false)} className={isActive('/shop')}>Shop</Link>
              <Link to="/about" onClick={() => setIsMenuOpen(false)} className={isActive('/about')}>About Shep</Link>
              <Link to="/rules" onClick={() => setIsMenuOpen(false)} className={isActive('/rules')}>Rules</Link>
              <Link to="/pricing" onClick={() => setIsMenuOpen(false)} className={isActive('/pricing')}>Pricing</Link>
              <Link to="/contact" onClick={() => setIsMenuOpen(false)} className={isActive('/contact')}>Contact</Link>
              <Link 
                to="/app" 
                onClick={() => setIsMenuOpen(false)}
                className="bg-emerald-600 text-white px-8 py-3 rounded-full font-bold w-full text-center"
              >
                Login / Book
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content Area */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-emerald-800 text-white pt-10 pb-6">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-display text-xl mb-4">Shep's Sniff Spot</h3>
              <p className="text-emerald-100 text-sm leading-relaxed">
                A safe, secure, and private sanctuary for dogs to run, sniff, and be free.
              </p>
            </div>
            <div>
              <h3 className="font-display text-xl mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-emerald-100">
                <li><Link to="/rules" className="hover:text-white">Safety Rules</Link></li>
                <li><Link to="/shop" className="hover:text-white">Shop</Link></li>
                <li><Link to="/app" className="hover:text-white">Member Login</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-xl mb-4">Connect</h3>
              <div className="flex space-x-4">
                {/* Updated Links to fix warnings */}
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-emerald-700 p-2 rounded-full hover:bg-emerald-600 transition"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
                <a 
                  href="https://www.facebook.com/share/1J3QJquGUS/?mibextid=wwXIfr" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="bg-emerald-700 p-2 rounded-full hover:bg-emerald-600 transition"
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a>
                <a 
                  href="mailto:woof@shepssniffspot.co.za" 
                  className="bg-emerald-700 p-2 rounded-full hover:bg-emerald-600 transition"
                  aria-label="Email Us"
                >
                  <Mail size={20} />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-emerald-700 pt-6 text-center text-sm text-emerald-300">
            © {new Date().getFullYear()} Shep's Sniff Spot. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;